<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useErrorLogger } from '@/composables/useErrorLogger';
import { useBarcodeLabel } from '@/composables/useBarcodeLabel';
import axios from 'axios';

const props = defineProps({
  token: { type: String, required: true },
  branchId: { type: String, required: true },
});

const route = useRoute();
const router = useRouter();
const toast = useToast();

// URL sync only applies on the standalone search page — this component also renders
// inside the POS popup (on '/'), where searches must not rewrite the URL.
const isStandaloneSearchPage = computed(() => route.path.startsWith('/checkinventory'));

const syncQueryToUrl = () => {
  if (!isStandaloneSearchPage.value) return;
  const query = { ...route.query };
  if (searchQuery.value) query.q = searchQuery.value;
  else delete query.q;
  router.replace({ query });
};

const copySearchLink = () => {
  const q = searchQuery.value.trim();
  if (!q) {
    toast.error('Enter a search first');
    return;
  }
  copyToClipboard(`${window.location.origin}/itemsearch?q=${encodeURIComponent(q)}`, 'Search link');
};
const { logError } = useErrorLogger();
const { printProduct, isPrinting } = useBarcodeLabel();

const searchQuery = ref('');   // the search box value; kept after Enter but selected so the next scan replaces it
const searchInput = ref(null); // template ref to the <input> (to select-all after a search / on focus)
const minCharacters = 3;

// ── Pagination / preloading ────────────────────────────────────────────────
// ERPNext's get_item_by_retail_sku returns a fixed 10 rows per call regardless of the
// page_size we send, so we can't grow the batch server-side. We compensate on the client:
// fetch several server pages *in parallel* to fill the first screen, then keep one page
// buffered ahead of the scroll. INITIAL_PAGES = pages fetched on a new search (3 to fill +
// 1 buffer = 40 items). PAGES_PER_LOAD = pages pulled per scroll trigger (reached + 1
// ahead). MAX_AUTO_ITEMS caps auto-loading so a broad query can't silently pull thousands.
// Paged read-through to ERPNext (no DB writes). Small pages return fast and never hit
// ERPNext's 90s gateway timeout, so we can accumulate a large set via infinite scroll.
const pageSize = ref(25);           // items per page (ERPNext honours page_size)
const INITIAL_PAGES = 2;            // pages fetched in parallel on a new search (≈50 items)
const PAGES_PER_LOAD = 2;           // pages pulled per scroll trigger (reached + 1 buffered ahead)
const MAX_AUTO_ITEMS = 5000;        // auto-load ceiling; beyond it, a manual "Load more"

const responseData = ref([]);
const nextPage = ref(1);            // next server page number not yet loaded
const hasMoreData = ref(true);      // false once an empty page confirms the end
const hasSearched = ref(false);     // a search has completed at least once (for empty state)
const loadError = ref(false);       // last fetch failed → surface Retry

const isRequsetBeingMade = ref(false); // any fetch in flight (name kept: referenced widely)
const isPreloading = ref(false);       // initial parallel fill in progress
const isLoadingMore = ref(false);      // appending more pages in progress

const atAutoCap = computed(() => responseData.value.length >= MAX_AUTO_ITEMS);

// Ignore responses from superseded searches, and abort their in-flight requests.
let searchGen = 0;
let abortController = null;
// Tags from API can be a comma-separated string or an array
const getProductTags = (product) => {
  if (!product?.Tags) return [];
  if (Array.isArray(product.Tags)) return product.Tags.map(t => String(t).trim()).filter(Boolean);
  return String(product.Tags).split(',').map(t => t.trim()).filter(Boolean);
};

// ── Tag scroll strip ──────────────────────────────────────────────────────
const tagRefs = {};
const tagScrollStates = reactive({});

const setTagRef = (el, sku) => { if (el) tagRefs[sku] = el; };

const updateScrollState = (sku) => {
  const el = tagRefs[sku];
  if (!el) return;
  const canScrollLeft = el.scrollLeft > 2;
  const containerRight = el.getBoundingClientRect().right;
  let hiddenCount = 0;
  el.querySelectorAll('[data-tag]').forEach(t => {
    if (t.getBoundingClientRect().left >= containerRight - 2) hiddenCount++;
  });
  tagScrollStates[sku] = { canScrollLeft, hiddenCount };
};

const refreshAllTagStates = async () => {
  await nextTick();
  responseData.value.forEach(p => updateScrollState(p.Sku));
};

const scrollTagsRight = (sku) => {
  const el = tagRefs[sku];
  if (!el) return;
  el.scrollBy({ left: 160, behavior: 'smooth' });
  setTimeout(() => updateScrollState(sku), 350);
};

const scrollTagsLeft = (sku) => {
  const el = tagRefs[sku];
  if (!el) return;
  el.scrollBy({ left: -160, behavior: 'smooth' });
  setTimeout(() => updateScrollState(sku), 350);
};
// ─────────────────────────────────────────────────────────────────────────

// Low-level: fetch ONE server page. Returns { products, status }. Throws on network error
// or abort (callers distinguish an abort via signal.aborted).
const fetchPage = async (retailSku, pageNum, signal) => {
  let url = `/api/Order/GetProductsByRetailSkuPaged?retailSku=${encodeURIComponent(retailSku)}&page=${pageNum}&pageSize=${pageSize.value}`;
  if (warehouseFilter.value) url += `&warehouse=${encodeURIComponent(warehouseFilter.value)}`; // server-side ranking
  const response = await axios.get(
    url,
    { headers: { 'store-id': props.branchId, 'Authorization': `Bearer ${props.token}` }, signal }
  );
  return { products: response.data.Products || [], status: response.data.Status, total: response.data.Total ?? 0 };
};

// Merge parallel page results in order, stopping at the first empty page (= end of results).
const mergePages = (results) => {
  const items = [];
  let consumed = 0;
  for (const r of results) {
    if (r.status === 200 && r.products.length > 0) { items.push(...r.products); consumed++; }
    else break;
  }
  return { items, consumed, ended: consumed < results.length };
};

// Start a fresh generation, aborting any in-flight requests from the previous search.
const beginGeneration = () => {
  if (abortController) abortController.abort();
  abortController = new AbortController();
  return { gen: ++searchGen, signal: abortController.signal };
};
const isStale = (gen) => gen !== searchGen;

const logFetchError = (error, action) => {
  logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'CheckInventoryContent', action, retailSku: searchQuery.value, branchId: props.branchId } });
};


// New search: reset state and fill the first screen with INITIAL_PAGES fetched concurrently.
// The rest loads on demand via the infinite-scroll observer (loadMore).
const runSearch = async () => {
  const { gen, signal } = beginGeneration();
  isRequsetBeingMade.value = true;
  isPreloading.value = true;
  loadError.value = false;
  hasMoreData.value = true;
  responseData.value = [];
  nextPage.value = 1;
  expandedGroups.value = {};

  try {
    const pageNums = Array.from({ length: INITIAL_PAGES }, (_, i) => i + 1);
    const results = await Promise.all(pageNums.map(p => fetchPage(searchQuery.value, p, signal)));
    if (isStale(gen)) return;

    const { items, consumed, ended } = mergePages(results);
    responseData.value = items;
    totalCount.value = results.find(r => r?.total)?.total ?? items.length;
    nextPage.value = consumed + 1;
    hasMoreData.value = !ended;
    hasSearched.value = true;
    await refreshAllTagStates();
  } catch (error) {
    if (signal.aborted || isStale(gen)) return; // superseded → ignore
    loadError.value = true;
    toast.error('Error fetching products');
    logFetchError(error, 'runSearch');
  } finally {
    if (!isStale(gen)) { isRequsetBeingMade.value = false; isPreloading.value = false; }
  }
};

// Append the next batch: the page the user reached plus one buffered ahead. `override`
// bypasses the MAX_AUTO_ITEMS cap for the manual "Load more" button.
const loadMore = async ({ override = false } = {}) => {
  // isPreloading is on while the eager bulk loader runs — don't race it on nextPage.
  if (!hasMoreData.value || isRequsetBeingMade.value || isPreloading.value || loadError.value) return;
  if (!override && atAutoCap.value) return;

  const gen = searchGen;
  const signal = abortController?.signal;
  isRequsetBeingMade.value = true;
  isLoadingMore.value = true;

  try {
    const start = nextPage.value;
    const pageNums = Array.from({ length: PAGES_PER_LOAD }, (_, i) => start + i);
    const results = await Promise.all(pageNums.map(p => fetchPage(searchQuery.value, p, signal)));
    if (isStale(gen)) return;

    const { items, consumed, ended } = mergePages(results);
    if (items.length) responseData.value = [...responseData.value, ...items];
    nextPage.value = start + consumed;
    if (ended) hasMoreData.value = false;
    await refreshAllTagStates();
  } catch (error) {
    if (signal?.aborted || isStale(gen)) return;
    loadError.value = true;
    logFetchError(error, 'loadMore');
  } finally {
    if (!isStale(gen)) { isRequsetBeingMade.value = false; isLoadingMore.value = false; }
    reobserveSentinel(); // re-arm the observer if the sentinel is still in view after appending
  }
};

// Retry after an error: re-run the whole search if nothing loaded, else resume loading more.
const retryLoad = () => {
  loadError.value = false;
  if (!responseData.value.length) runSearch();
  else loadMore({ override: true });
};

function handleEnterKey() {
  const query = searchQuery.value.trim();

  if (/^\d+$/.test(query)) {
    if (query.length < 12 || query.length > 13) {
      toast.error('Barcode must 12 or 13 digits.');
      return;
    }
  } else {
    if (query.length < minCharacters) {
      toast.error(`Please enter at least ${minCharacters} characters to search the full catalog.`);
      return;
    }
  }

  searchQuery.value = query.replace(/\s+/g, '');
  syncQueryToUrl();
  runSearch();
  // Keep the term visible (so copy-link + context work) but select it, so the next scan or
  // keystroke replaces the whole thing instead of appending to it.
  nextTick(() => searchInput.value?.select());
}

// Touching the field after a search selects the term, so the next scan replaces it.
const selectSearchOnFocus = () => { if (hasSearched.value) nextTick(() => searchInput.value?.select()); };

function clearSearch() {
  if (abortController) abortController.abort();
  searchGen++; // invalidate any in-flight responses
  searchQuery.value = '';
  responseData.value = [];
  totalCount.value = 0;
  warehouseFilter.value = '';
  hasMoreData.value = true;
  hasSearched.value = false;
  loadError.value = false;
  isRequsetBeingMade.value = false;
  isPreloading.value = false;
  isLoadingMore.value = false;
  syncQueryToUrl();
}

// Longest common prefix of a group's variant names → the parent "product" name (falls back
// to the template id). Trailing separators are trimmed ("Fleece Pant - " → "Fleece Pant").
const groupDisplayName = (products) => {
  const names = products.map(p => p.ItemName || '').filter(Boolean);
  if (!names.length) return products[0]?.TemplateId || '';
  let prefix = names[0];
  for (let k = 1; k < names.length; k++) {
    const n = names[k];
    let i = 0;
    while (i < prefix.length && i < n.length && prefix[i] === n[i]) i++;
    prefix = prefix.slice(0, i);
    if (!prefix) break;
  }
  prefix = prefix.replace(/[\s\-–—•,:/|]+$/, '').trim();
  return prefix || names[0];
};

// Products grouped by TemplateId — one "product" per group with its variants underneath.
// Both views render these: a group collapses to a header (name + variant count) and
// expands on click to reveal its variants.
// ── Warehouse ranking (server-side) + grouped/flat toggle ───────────────────────────────
// Selecting a warehouse re-runs the search with &warehouse=<name>; ERPNext ranks the whole
// result set by that warehouse's stock (most-stocked first). It's a sort, not a filter.
const warehouseFilter = ref('');   // '' = default order (no warehouse ranking)
// Remembered view preferences (grouped/flat + list/card), restored across reloads/searches.
const VIEW_PREFS = (() => { try { return JSON.parse(localStorage.getItem('ci_viewPrefs') || '{}'); } catch { return {}; } })();
const groupMode = ref(VIEW_PREFS.groupMode ?? true); // true = grouped by template, false = flat list
const totalCount = ref(0);         // total matches ERPNext reports (across all pages)

// Stable warehouse list (canonical order) — kept constant so the dropdown doesn't flicker
// while a warehouse-change re-run clears responseData.
const warehouses = computed(() => customBranchOrder);

// Re-run the search when the warehouse ranking changes — the server returns a re-ordered set.
watch(warehouseFilter, () => {
  if (searchQuery.value && hasSearched.value) runSearch();
});

// Per-template background colour (preserves load/rank order); used by group + flat rendering.
const coloredProducts = computed(() => {
  const bgColors = [
    'bg-yellow-100', 'bg-green-100', 'bg-blue-100', 'bg-purple-100',
    'bg-pink-100', 'bg-indigo-100', 'bg-red-100', 'bg-gray-200'
  ];
  const colorByTemplate = {};
  let ci = 0;
  return responseData.value.map(p => {
    if (!(p.TemplateId in colorByTemplate)) colorByTemplate[p.TemplateId] = bgColors[ci++ % bgColors.length];
    p._bgColor = colorByTemplate[p.TemplateId];
    return p;
  });
});

const groups = computed(() => {
  const map = new Map();
  for (const product of coloredProducts.value) {
    const id = product.TemplateId;
    if (!map.has(id)) map.set(id, { templateId: id, products: [], bgColor: product._bgColor });
    map.get(id).products.push(product);
  }
  return Array.from(map.values()).map(g => ({
    ...g,
    count: g.products.length,
    name: groupDisplayName(g.products),
    image: g.products.find(p => p.ImageUrl)?.ImageUrl || g.products[0]?.ImageUrl || '',
  }));
});

// Expand/collapse state keyed by templateId — every group starts collapsed (reset each
// search); click a header or "Expand all" to open.
const expandedGroups = ref({});
const isGroupExpanded = (group) => !!expandedGroups.value[group.templateId];
const toggleGroup = (group) => {
  expandedGroups.value = { ...expandedGroups.value, [group.templateId]: !isGroupExpanded(group) };
};
const expandAll = () => {
  const map = {};
  for (const g of groups.value) map[g.templateId] = true;
  expandedGroups.value = map;
};
const collapseAll = () => {
  const map = {};
  for (const g of groups.value) map[g.templateId] = false;
  expandedGroups.value = map;
};
const allGroupsExpanded = computed(() => groups.value.length > 0 && groups.value.every(g => isGroupExpanded(g)));
const toggleAll = () => { allGroupsExpanded.value ? collapseAll() : expandAll(); };

const customBranchOrder = ['WHS', 'DTN', 'GOR', 'EDM', 'VIC', 'CLI', 'QEN', 'HUB', 'BER', 'OSH', 'TEX'];

const sortBranches = (branches = []) => {
  const filtered = branches.filter(branch => customBranchOrder.includes(branch.DisplayName));
  const uniqueBranches = [];
  const seenDisplayNames = new Set();
  for (const branch of filtered) {
    if (!seenDisplayNames.has(branch.DisplayName)) {
      seenDisplayNames.add(branch.DisplayName);
      uniqueBranches.push(branch);
    }
  }
  return uniqueBranches.sort((a, b) =>
    customBranchOrder.indexOf(a.DisplayName) - customBranchOrder.indexOf(b.DisplayName)
  );
};

// Every warehouse (canonical order) with the product's qty for it — 0 when the product has
// no row for that warehouse. Rendered as fixed-width columns so quantities line up
// vertically across rows regardless of which warehouses a given item stocks.
const warehouseColumns = (product) => {
  const byName = {};
  for (const b of (product.Branches || [])) if (b?.DisplayName) byName[b.DisplayName] = b;
  return customBranchOrder.map(name => ({ name, qty: Number(byName[name]?.Qty ?? 0), branch: byName[name] || null }));
};

const selectedSku = ref(null);
const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.success(`${label} copied!`);
  }).catch(() => {
    toast.error(`Failed to copy ${label}`);
  });
};

const printBarcode = async (product, count = 1) => {
  for (let i = 0; i < count; i++) {
    await printProduct(product);
    if (i < count - 1) await new Promise(r => setTimeout(r, 120));
  }
};

const printTarget = ref(null);
let printCountInputEl = null;

// ── Barcode copy / print, with a picker when an item has more than one barcode ──────────
const barcodeCount = (p) => p.Barcodes?.length || 0;
// { sku, mode: 'copy' | 'print' } — which product's picker is open, and what it will do.
const barcodeMenu = ref(null);

function onCopyBarcodeClick(product) {
  const n = barcodeCount(product);
  if (n === 0) return;                                              // disabled
  if (n === 1) copyToClipboard(product.Barcodes[0].Barcode, 'Barcode');
  else barcodeMenu.value = { sku: product.Sku, mode: 'copy' };      // let the user pick
}
function onPrintBarcodeClick(product) {
  const n = barcodeCount(product);
  if (n === 0) return;                                              // disabled
  if (n === 1) printBarcode(product, 1);
  else barcodeMenu.value = { sku: product.Sku, mode: 'print' };     // let the user pick
}
// Act on the chosen barcode from the picker (printing a specific barcode = a product copy
// whose only barcode is the selected one).
async function chooseBarcode(product, barcode) {
  const mode = barcodeMenu.value?.mode;
  barcodeMenu.value = null;
  if (mode === 'copy') copyToClipboard(barcode, 'Barcode');
  else if (mode === 'print') await printBarcode({ ...product, Barcodes: [{ Barcode: barcode }], barcode }, 1);
}
const closeBarcodeMenu = () => { barcodeMenu.value = null; };

function onPrintClick(product) {
  if (!barcodeCount(product)) return;
  onPrintBarcodeClick(product);
}

async function onPrintMultipleClick(product) {
  printTarget.value = { product, count: 3 };
  await nextTick();
  printCountInputEl?.focus();
  printCountInputEl?.select();
}

async function confirmPrintWithCount() {
  if (!printTarget.value) return;
  const { product, count } = printTarget.value;
  printTarget.value = null;
  await printBarcode(product, Math.max(1, count));
}

const hasAnyValidCosts = computed(() =>
  responseData.value.some(product => {
    const cost = product.Cost;
    return cost && cost !== '-' && !isNaN(parseFloat(cost));
  })
);

const getGorillaPrice = (product) => product.Branches?.find(b => b.PriceList === 'RET - Gorilla')?.Price ?? null;
const getCamoPrice   = (product) => product.Branches?.find(b => b.PriceList === 'RET - Camo')?.Price ?? null;
const getTexasPrice  = (product) => product.Branches?.find(b => b.PriceList === 'RET - CamoUSA')?.Price ?? null;

// In the POS popup a branch is paired, so show that branch's price (ERPNext's top-level
// product.Price, computed for the store context). On the standalone /checkinventory tool
// there's no POS branch, so we show the Gorilla + Camo reference prices instead.
const inPos = computed(() => !isStandaloneSearchPage.value);
const currentBranchPrice = (product) => product.Price;
const formatPrice = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? '—' : `$${Number(v).toFixed(2)}`;

const viewMode = ref(VIEW_PREFS.viewMode === 'card' ? 'card' : 'list');

// Persist the grouped/flat + list/card choices so they carry across searches and reloads.
watch([groupMode, viewMode], ([g, v]) => {
  try { localStorage.setItem('ci_viewPrefs', JSON.stringify({ groupMode: g, viewMode: v })); } catch { /* ignore */ }
});
const cardContainerRef = ref(null);
const listContainerRef = ref(null);
const getActiveContainer = () => viewMode.value === 'card' ? cardContainerRef.value : listContainerRef.value;

// ── Infinite scroll via IntersectionObserver (with a throttled scroll fallback) ──────────
// A sentinel at the bottom of the active view triggers loadMore() when it comes within
// 600px of the viewport. IntersectionObserver only fires on intersection *changes*, so a
// rAF-throttled scroll listener backs it up for the case where the sentinel stays in view
// after content appends. getActiveContainer() is defined above.
const listSentinel = ref(null);
const cardSentinel = ref(null);
const getActiveSentinel = () => viewMode.value === 'card' ? cardSentinel.value : listSentinel.value;

let io = null;
const setupObserver = async () => {
  await nextTick();
  if (io) io.disconnect();
  const root = getActiveContainer();
  const sentinel = getActiveSentinel();
  if (!root || !sentinel) return;
  io = new IntersectionObserver(
    (entries) => { if (entries.some(e => e.isIntersecting)) loadMore(); },
    { root, rootMargin: '600px 0px' }
  );
  io.observe(sentinel);
};

// Force the observer to re-evaluate after a load appends content while the sentinel may
// still be within the trigger margin.
const reobserveSentinel = () => {
  if (!io) return;
  const sentinel = getActiveSentinel();
  if (!sentinel) return;
  io.unobserve(sentinel);
  io.observe(sentinel);
};

let scrollScheduled = false;
const onScroll = () => {
  if (scrollScheduled) return;
  scrollScheduled = true;
  requestAnimationFrame(() => {
    scrollScheduled = false;
    const el = getActiveContainer();
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 600) loadMore();
  });
};

const attachScroll = async () => {
  await nextTick();
  const el = getActiveContainer();
  if (!el) return;
  el.removeEventListener('scroll', onScroll);
  el.addEventListener('scroll', onScroll);
};

const setupInfiniteScroll = () => { setupObserver(); attachScroll(); };

// Close the barcode picker on any click outside it (clicks inside stop propagation).
const onDocClick = () => { if (barcodeMenu.value) barcodeMenu.value = null; };

onMounted(() => {
  setupInfiniteScroll();
  refreshAllTagStates();
  document.addEventListener('click', onDocClick);
  // Shareable links (?q=<term>): this component only mounts once logged in, so this
  // also fires right after the inline login — the term survives the login screen.
  const q = typeof route.query.q === 'string' ? route.query.q.trim() : '';
  if (q) {
    searchQuery.value = q;
    handleEnterKey();
  }
});
watch(viewMode, () => { setupInfiniteScroll(); refreshAllTagStates(); });
onUnmounted(() => {
  if (io) io.disconnect();
  document.removeEventListener('click', onDocClick);
  const cardEl = cardContainerRef.value;
  const listEl = listContainerRef.value;
  if (cardEl) cardEl.removeEventListener('scroll', onScroll);
  if (listEl) listEl.removeEventListener('scroll', onScroll);
  if (abortController) abortController.abort();
});
watch(responseData, async (newVal) => {
  if (newVal.length > 0) {
    await nextTick();
    setupInfiniteScroll();
    refreshAllTagStates();
  }
});

// Parse "3 (ETA: 03-May-26)" → { qty: '3', eta: '03-May-26' }
const parseOnOrderQty = (raw) => {
  if (!raw) return null;
  const m = raw.match(/^(.+?)\s*\(ETA:\s*(.+?)\)$/);
  return m ? { qty: m[1].trim(), eta: m[2].trim() } : { qty: raw.trim(), eta: null };
};

const formatReconciliationDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .format(date).replace(/ /g, '-');
};

const borderColorMap = {
  A: 'border-pink-400', B: 'border-green-400', C: 'border-blue-400', D: 'border-yellow-400',
  E: 'border-purple-400', F: 'border-red-400', G: 'border-indigo-400', H: 'border-teal-400',
  I: 'border-cyan-400', J: 'border-lime-400', K: 'border-amber-400', L: 'border-orange-400',
  M: 'border-fuchsia-400', N: 'border-rose-400', O: 'border-sky-400', P: 'border-violet-400',
  Q: 'border-emerald-400', R: 'border-yellow-600', S: 'border-blue-600', T: 'border-purple-600',
  U: 'border-pink-600', V: 'border-green-600', W: 'border-orange-600', X: 'border-red-600',
  Y: 'border-teal-600', Z: 'border-indigo-600'
};

const getItemClassBorder = (itemClass) => {
  if (!itemClass || itemClass.length === 0) return 'border-gray-300';
  return borderColorMap[itemClass.charAt(0).toUpperCase()] || 'border-gray-300';
};
</script>

<template>
  <div class="flex items-center gap-2 w-full">
    <div class="relative flex-1">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        autocomplete="off"
        name="barcode-search"
        id="barcode-search"
        class="bg-gray-100 border-0 outline-none p-2 rounded-lg w-full pr-10"
        placeholder="Search by Retail Sku"
        @keydown.enter="handleEnterKey"
        @focus="selectSearchOnFocus"
      />
      <button v-if="searchQuery" @click="clearSearch"
        class="absolute right-2 top-2 text-gray-500 hover:text-gray-700">
        <i class="pi pi-times pr-2"></i>
      </button>
    </div>
    <div class="relative group">
      <button
        class="p-2 rounded-lg bg-gray-100 text-purple-600 hover:text-gray-800"
        @click="copySearchLink">
        <i class="pi pi-link" style="font-size: 16px;"></i>
      </button>
      <div class="tooltip-tip tooltip-tip--left">Copy search link</div>
    </div>
  </div>

  <div class="flex items-center justify-between gap-2 py-4 flex-wrap">
    <div class="flex items-center gap-2 flex-wrap">
      <p class="font-semibold text-lg">Order By Total Qty</p>
      <button><i class="pi pi-sort"></i></button>
      <i class="pi pi-sort-amount-down-alt"></i>

      <!-- Warehouse ranking: re-runs the search, ordering results by that warehouse's stock -->
      <select v-if="hasSearched" v-model="warehouseFilter"
        title="Rank results by stock at a warehouse"
        class="ml-2 text-sm border rounded-lg px-2 py-1 bg-white outline-none">
        <option value="">All warehouses</option>
        <option v-for="w in warehouses" :key="w" :value="w">Most stock at {{ w }}</option>
      </select>

      <!-- Grouped / Flat toggle -->
      <div v-if="hasSearched" class="ml-1 flex rounded-lg border overflow-hidden text-sm">
        <button class="px-2.5 py-1" :class="groupMode ? 'bg-purple-100 font-semibold text-purple-700' : 'bg-white text-gray-600'" @click="groupMode = true">Grouped</button>
        <button class="px-2.5 py-1 border-l" :class="!groupMode ? 'bg-purple-100 font-semibold text-purple-700' : 'bg-white text-gray-600'" @click="groupMode = false">Flat</button>
      </div>

      <span v-if="totalCount" class="text-xs text-gray-500">{{ totalCount }} result{{ totalCount === 1 ? '' : 's' }}</span>
    </div>
    <div class="flex gap-2 items-center">
      <!-- Expand / collapse every group at once -->
      <button
        v-if="hasSearched && groupMode"
        @click="toggleAll"
        class="px-3 py-1 rounded border text-sm bg-white text-gray-600 hover:bg-gray-50 whitespace-nowrap">
        {{ allGroupsExpanded ? 'Collapse all' : 'Expand all' }}
      </button>
      <!-- Animated loading indicator: visible during any fetch or background preload -->
      <span
        v-if="isRequsetBeingMade || isPreloading"
        class="inline-block w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"
        title="Loading…"
        aria-label="Loading"
        role="status"
      ></span>
      <button
        class="px-3 py-1 rounded border text-sm"
        :class="viewMode === 'list' ? 'bg-purple-100 font-semibold' : 'bg-white'"
        @click="viewMode = 'list'"
      >
        <i class="pi pi-bars"></i>
      </button>
      <button
        class="px-3 py-1 rounded border text-sm"
        :class="viewMode === 'card' ? 'bg-purple-100 font-semibold' : 'bg-white'"
        @click="viewMode = 'card'"
      >
        <i class="pi pi-th-large"></i>
      </button>
    </div>
  </div>

  <!-- First-load skeleton rows -->
  <div v-if="isRequsetBeingMade && !responseData.length" class="py-4" aria-busy="true" role="status" aria-label="Searching">
    <div class="elastic-track mb-2"><div class="elastic-bar"></div></div>
    <p class="text-center text-xs text-gray-400 mb-3">Searching…</p>
    <div v-for="n in 6" :key="n" class="flex items-center gap-3 py-3 border-b border-gray-100">
      <div class="skeleton w-12 h-12 rounded-md flex-shrink-0"></div>
      <div class="flex-1 space-y-2">
        <div class="skeleton h-3 rounded" style="width:40%"></div>
        <div class="skeleton h-3 rounded" style="width:25%"></div>
      </div>
      <div class="skeleton h-6 w-24 rounded-full"></div>
    </div>
  </div>

  <!-- Error on first load (nothing to show yet) -->
  <div v-else-if="loadError && !responseData.length" class="py-10 text-center">
    <p class="text-red-500 mb-3">Couldn't load products.</p>
    <button @click="retryLoad" class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Retry</button>
  </div>

  <!-- Empty results -->
  <div v-else-if="hasSearched && !responseData.length" class="py-10 text-center text-gray-500">
    No products found for “{{ searchQuery }}”.
  </div>

  <div v-else-if="responseData.length > 0" :aria-busy="isRequsetBeingMade" class="">
    <div class="gap-4">

      <!-- List View -->
      <div v-if="viewMode === 'list'">
        <div class="">
          <div class="overflow-y-auto overflow-x-hidden p-2" ref="listContainerRef" style="max-height: 670px;">
            <!-- border-separate overrides DaisyUI's global border-collapse so the group
                 boxes' rounded corners (cell border-radius) actually render. -->
            <table class="w-full bg-white text-sm border-separate border-spacing-0">
              <thead class="bg-gray-100 sticky -top-2 z-10">
                <tr>
                  <th class="text-left p-2 border-b bg-gray-200">Image</th>
                  <th class="text-left p-2 border-b bg-gray-200">Item Name</th>
                  <th class="text-left p-2 border-b bg-gray-200">RetailSku</th>
                  <th class="text-left p-2 border-b bg-gray-200">Barcode</th>
                  <th v-if="hasAnyValidCosts" class="text-left p-2 border-b bg-gray-200">Cost</th>
                  <th v-if="inPos" class="text-left p-2 border-b bg-gray-200">Price</th>
                  <th v-if="!inPos" class="text-left p-2 border-b bg-gray-200">Gor Price</th>
                  <th v-if="!inPos" class="text-left p-2 border-b bg-gray-200">Camo Price</th>
                  <th class="text-left p-2 border-b bg-gray-200">Location</th>
                  <th class="text-left p-2 border-b bg-gray-200">TemplateId</th>
                </tr>
              </thead>
              <!-- One <tbody> per product with content-visibility:auto — the browser skips
                   layout/paint for rows scrolled off-screen, so the DOM stays cheap as the
                   list grows (lightweight virtualization, no dependency / structural rewrite). -->
              <tbody v-for="group in groups" :key="group.templateId" :class="['ci-vrow', groupMode && 'ci-group', groupMode && isGroupExpanded(group) && 'ci-group--open']">
                  <!-- Group header (grouped mode only; click to expand/collapse the variants) -->
                  <tr v-if="groupMode" @click="toggleGroup(group)"
                      :class="['cursor-pointer select-none', isGroupExpanded(group) ? 'bg-purple-50' : 'bg-white hover:bg-purple-50']">
                    <td class="px-2 py-2">
                      <img :src="group.image" alt="Img" class="w-12 h-12 object-cover rounded-md" />
                    </td>
                    <td class="px-2" colspan="11">
                      <div class="flex items-center gap-3">
                        <i :class="isGroupExpanded(group) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" class="text-gray-500"></i>
                        <div class="min-w-0">
                          <div class="font-semibold whitespace-normal break-words leading-tight line-clamp-2" :title="group.name">{{ group.name }}</div>
                          <span :class="[group.bgColor, 'text-xs px-2 py-0.5 rounded-full inline-block mt-0.5']">{{ group.templateId }}</span>
                        </div>
                        <span class="ml-auto text-xs font-medium bg-purple-100 text-purple-700 px-2 py-1 rounded-full whitespace-nowrap">{{ group.count }} {{ group.count === 1 ? 'item' : 'items' }}</span>
                      </div>
                    </td>
                  </tr>
                  <template v-if="!groupMode || isGroupExpanded(group)">
                  <template v-for="(product, index) in group.products" :key="product.Sku">
                  <tr :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-200'">
                    <td class="px-2">
                      <img :src="product.ImageUrl" alt="Img" class="w-12 h-12 object-cover rounded-md" />
                    </td>
                    <td class="px-2 max-w-[240px] align-top relative overflow-hidden" :title="product.ItemName">
                      <!-- Faint item-class letter watermark (background class) -->
                      <span class="absolute right-1 top-1/2 -translate-y-1/2 text-6xl font-bold opacity-[0.07] pointer-events-none select-none leading-none">{{ product.ItemClass?.[0] }}</span>
                      <div class="relative z-10 whitespace-normal break-words leading-tight line-clamp-2">
                        {{ product.ItemName }}
                      </div>
                      <span v-if="product.Discontinued" class="relative z-10 inline-block mt-0.5 text-red-500 bg-red-200 px-1 rounded text-xs">Discontinued</span>
                      <div v-if="getProductTags(product).length" class="relative mt-1" style="height:20px;">
                        <div class="absolute inset-0 flex items-center gap-1">
                          <button
                            v-if="tagScrollStates[product.Sku]?.canScrollLeft"
                            @click.stop="scrollTagsLeft(product.Sku)"
                            class="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-sm leading-none select-none"
                          >‹</button>
                          <div
                            :ref="el => setTagRef(el, product.Sku)"
                            class="tag-strip flex-1 min-w-0 flex flex-nowrap gap-1"
                            @scroll="updateScrollState(product.Sku)"
                          >
                            <span
                              v-for="tag in getProductTags(product)"
                              :key="tag"
                              data-tag
                              :title="tag"
                              class="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium whitespace-nowrap flex-shrink-0"
                            >{{ tag }}</span>
                          </div>
                          <button
                            v-if="tagScrollStates[product.Sku]?.hiddenCount > 0"
                            @click.stop="scrollTagsRight(product.Sku)"
                            class="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium hover:bg-gray-300 select-none"
                          >+{{ tagScrollStates[product.Sku].hiddenCount }}</button>
                        </div>
                      </div>
                    </td>
                    <td class="px-2">{{ product.RetailSku }}</td>
                    <td class="px-2 align-top">
                      <!-- All barcodes listed when an item has more than one -->
                      <div v-for="(bc, bi) in (product.Barcodes || [])" :key="bc.Barcode + bi"
                        :class="bi === 0 ? '' : 'text-xs text-gray-500'">{{ bc.Barcode }}</div>
                      <span v-if="!barcodeCount(product)" class="text-xs text-gray-400 italic">No barcode</span>
                    </td>
                    <td v-if="hasAnyValidCosts" class="px-2">{{ product.Cost && product.Cost !== '-' ? `$${parseFloat(product.Cost).toFixed(2)}` : '-' }}</td>
                    <td v-if="inPos" class="px-2">{{ formatPrice(currentBranchPrice(product)) }}</td>
                    <td v-if="!inPos" class="px-2">{{ formatPrice(getGorillaPrice(product)) }}</td>
                    <td v-if="!inPos" class="px-2">{{ formatPrice(getCamoPrice(product)) }}</td>
                    <td class="px-2">{{ product.Location }}</td>
                    <td class="px-2 text-sm">
                      <span :class="[product._bgColor, 'px-2 py-1 rounded-full inline-block w-full']">
                        {{ product.TemplateId }}
                      </span>
                    </td>
                  </tr>

                  <tr :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-200'">
                    <td colspan="12" class="px-2">
                      <div v-if="product.Branches?.length" class="w-full flex items-center gap-1 pb-2">
                        <!-- On Order stays on the left -->
                        <div v-if="product.OnOrderQty" class="flex-shrink-0 flex items-center gap-1.5 bg-yellow-400 rounded py-1 px-2">
                          <p>On Order: {{ parseOnOrderQty(product.OnOrderQty).qty }}</p>
                          <div v-if="parseOnOrderQty(product.OnOrderQty).eta" class="relative group">
                            <span class="text-xs bg-yellow-600 text-white px-1.5 py-0.5 rounded cursor-default font-semibold">ETA</span>
                            <div class="tooltip-tip">{{ parseOnOrderQty(product.OnOrderQty).eta }}</div>
                          </div>
                        </div>

                        <!-- Inventory block, right-aligned; fixed-width columns so they line up vertically across rows -->
                        <div class="ml-auto flex items-center gap-1">
                          <div
                            v-for="wh in warehouseColumns(product)" :key="wh.name"
                            class="w-[58px] flex-shrink-0 text-center px-1 py-1 rounded text-xs font-medium whitespace-nowrap"
                            :class="{
                              'bg-emerald-50 text-green-700': wh.qty > 5,
                              'bg-cyan-50 text-cyan-700': wh.qty > 0 && wh.qty <= 5,
                              'bg-gray-50 text-gray-400': wh.qty <= 0
                            }"
                            :title="wh.branch?.LastReconciliation ? `Reconciled on ${formatReconciliationDate(wh.branch.LastReconciliation)}` : 'Not reconciled'"
                          >{{ wh.name }}: {{ wh.qty }}</div>
                          <!-- Supplier quantity on hand — styled like a warehouse column -->
                          <div
                            v-if="product.SupplierQoh !== undefined && product.SupplierQoh !== null && product.SupplierQoh !== ''"
                            class="w-[66px] flex-shrink-0 text-center px-1 py-1 rounded text-xs font-medium whitespace-nowrap"
                            :class="{
                              'bg-emerald-50 text-green-700': Number(product.SupplierQoh) > 5,
                              'bg-cyan-50 text-cyan-700': Number(product.SupplierQoh) > 0 && Number(product.SupplierQoh) <= 5,
                              'bg-gray-50 text-gray-400': Number(product.SupplierQoh) <= 0
                            }"
                            title="Supplier quantity on hand"
                          >SQOH: {{ product.SupplierQoh }}</div>
                        </div>

                        <div class="relative group">
                          <button
                            :disabled="!barcodeCount(product)"
                            class="flex items-center gap-2 text-xl"
                            :class="barcodeCount(product) ? 'text-purple-600 hover:text-gray-800' : 'text-gray-300 cursor-not-allowed'"
                            @click.stop="onCopyBarcodeClick(product)">
                            <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-copy" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">{{ barcodeCount(product) > 1 ? 'Copy barcode…' : 'Copy Barcode' }}</div>
                          <!-- Picker: choose which barcode to copy -->
                          <div v-if="barcodeMenu?.sku === product.Sku && barcodeMenu?.mode === 'copy'"
                            class="absolute bottom-full right-0 mb-2 w-60 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-30 text-left" @click.stop>
                            <p class="text-[11px] text-gray-400 px-2 py-1">Select a barcode to copy</p>
                            <button v-for="bc in product.Barcodes" :key="bc.Barcode"
                              class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-purple-50"
                              @click="chooseBarcode(product, bc.Barcode)">
                              <span class="font-mono text-xs">{{ bc.Barcode }}</span>
                              <i class="pi pi-copy text-purple-600 text-xs"></i>
                            </button>
                          </div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="copyToClipboard(product.Sku, 'ItemCode')">
                            <span class="text-sm">Sku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Copy SKU</div>
                        </div>

                        <div class="relative group">
                          <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                            @click.stop="copyToClipboard(product.RetailSku, 'RetailSku')">
                            <span class="text-sm">RSku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Copy Retail SKU</div>
                        </div>

                        <div class="relative group">
                          <button
                            :disabled="!barcodeCount(product)"
                            class="flex items-center gap-2 text-xl"
                            :class="barcodeCount(product) ? 'text-purple-600 hover:text-gray-800' : 'text-gray-300 cursor-not-allowed'"
                            @click.stop="onPrintBarcodeClick(product)">
                            <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">{{ barcodeCount(product) > 1 ? 'Print barcode…' : 'Print barcode' }}</div>
                          <!-- Picker: choose which barcode to print -->
                          <div v-if="barcodeMenu?.sku === product.Sku && barcodeMenu?.mode === 'print'"
                            class="absolute bottom-full right-0 mb-2 w-60 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-30 text-left" @click.stop>
                            <p class="text-[11px] text-gray-400 px-2 py-1">Select a barcode to print</p>
                            <button v-for="bc in product.Barcodes" :key="bc.Barcode"
                              class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-purple-50"
                              @click="chooseBarcode(product, bc.Barcode)">
                              <span class="font-mono text-xs">{{ bc.Barcode }}</span>
                              <i class="pi pi-print text-purple-600 text-xs"></i>
                            </button>
                          </div>
                        </div>

                        <div class="relative group">
                          <button
                            :disabled="!barcodeCount(product)"
                            class="flex items-center gap-2 text-xl"
                            :class="barcodeCount(product) ? 'text-purple-600 hover:text-gray-800' : 'text-gray-300 cursor-not-allowed'"
                            @click.stop="onPrintMultipleClick(product)">
                            <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i><span class="text-xs font-semibold -ml-1">x</span>
                          </button>
                          <div class="tooltip-tip tooltip-tip--left">Print multiple barcodes</div>

                          <div v-if="printTarget?.product.Sku === product.Sku"
                            class="absolute bottom-full right-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-20"
                            @click.stop>
                            <input
                              :ref="el => { printCountInputEl = el }"
                              v-model.number="printTarget.count"
                              type="number" min="1" max="99"
                              class="w-14 px-2 py-1 bg-gray-100 rounded text-center text-sm outline-none"
                              @keydown.enter="confirmPrintWithCount"
                              @keydown.esc="printTarget = null"
                            />
                            <button class="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                              @click="confirmPrintWithCount">Print</button>
                            <button class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                              @click="printTarget = null">✕</button>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                  </template>
                  </template>
                  <!-- Transparent spacer so each template group reads as a separate card -->
                  <tr v-if="groupMode" class="ci-group-gap" aria-hidden="true"><td colspan="12"></td></tr>
              </tbody>
            </table>
            <!-- Sentinel: entering within 600px of the viewport triggers loadMore(). -->
            <div ref="listSentinel" class="h-px"></div>
            <div class="px-1 py-3" aria-live="polite">
              <!-- Error while loading more -->
              <div v-if="loadError" class="text-center">
                <button @click="retryLoad" class="px-4 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">Retry</button>
              </div>
              <!-- Loading / "more may exist" elastic bar (animates while fetching and while
                   it's still unknown whether more remain). -->
              <div v-else-if="isRequsetBeingMade || (hasMoreData && !atAutoCap)">
                <div class="elastic-track"><div class="elastic-bar"></div></div>
                <p v-if="isLoadingMore || isPreloading" class="text-center text-xs text-gray-400 mt-1">Loading… {{ responseData.length }} items</p>
              </div>
              <!-- Auto-load cap reached: continue manually -->
              <div v-else-if="hasMoreData && atAutoCap" class="text-center">
                <button @click="loadMore({ override: true })" class="px-4 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">Load more</button>
              </div>
              <!-- End of results -->
              <p v-else class="text-center text-xs text-gray-400">End of results</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Card View -->
      <div v-if="viewMode === 'card'">
        <div class="overflow-y-auto p-2" ref="cardContainerRef" style="max-height: 670px;">
          <div>
            <div v-for="group in groups" :key="group.templateId"
              :class="['w-full mb-4', groupMode ? ['rounded-2xl border-2 p-3 transition-colors', isGroupExpanded(group) ? 'border-purple-300' : 'border-gray-200'] : '']">
              <!-- Group header (grouped mode only; click to expand/collapse the variants) -->
              <div v-if="groupMode" @click="toggleGroup(group)"
                class="flex items-center gap-4 p-3 bg-gray-100 rounded-2xl cursor-pointer select-none hover:ring-2 hover:ring-purple-300 transition">
                <img :src="group.image" alt="Img" class="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                <div class="min-w-0 flex-1">
                  <p class="font-semibold line-clamp-2" :title="group.name">{{ group.name }}</p>
                  <span :class="[group.bgColor, 'text-xs px-2 py-0.5 rounded-full inline-block mt-0.5']">{{ group.templateId }}</span>
                </div>
                <span class="text-xs font-medium bg-purple-100 text-purple-700 px-3 py-1 rounded-full whitespace-nowrap">{{ group.count }} {{ group.count === 1 ? 'item' : 'items' }}</span>
                <i :class="isGroupExpanded(group) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" class="text-gray-500 text-lg"></i>
              </div>
              <div v-if="!groupMode || isGroupExpanded(group)" class="grid grid-cols-3 gap-4" :class="groupMode ? 'mt-4' : ''">
                <div v-for="product in group.products" :key="product.Sku" class="ci-vcard bg-gray-100 rounded-2xl">
                  <div @click="selectedSku = selectedSku === product.Sku ? null : product.Sku"
                    class="relative bg-gray-200 p-4 rounded-2xl cursor-pointer hover:ring-2 hover:ring-purple-300 transition">
                    <p class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-8xl font-bold opacity-10">{{ product.ItemClass[0] }}</p>
                    <div class="flex gap-4 items-start relative">
                      <img :src="product.ImageUrl" alt="Image" class="w-24 h-24 object-cover rounded-lg" />
                      <div class="flex-1" :class="[getItemClassBorder(product.ItemClass), 'border-l-4 pl-2']">
                        <div>
                          <p class="font-semibold">{{ product.ItemName }}</p>
                          <span v-if="product.Discontinued" class="text-red-500 bg-red-200 px-1 rounded">Discontinued</span>
                        </div>
                        <p class="text-sm text-gray-500">RetailSku: {{ product.RetailSku }}</p>
                        <p class="text-sm text-gray-500">Barcode:
                          <span v-if="!barcodeCount(product)" class="italic text-gray-400">No barcode</span>
                          <span v-else>{{ product.Barcodes[0].Barcode }}</span>
                        </p>
                        <p v-for="(bc, bi) in (product.Barcodes || []).slice(1)" :key="bc.Barcode + bi"
                          class="text-xs text-gray-400 ml-[62px] -mt-1">{{ bc.Barcode }}</p>
                        <p class="text-sm text-gray-500">Location: {{ product.Location }}</p>
                        <div v-if="getProductTags(product).length" class="relative mt-2" style="height:20px;">
                          <div class="absolute inset-0 flex items-center gap-1">
                            <button
                              v-if="tagScrollStates[product.Sku]?.canScrollLeft"
                              @click.stop="scrollTagsLeft(product.Sku)"
                              class="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-sm leading-none select-none"
                            >‹</button>
                            <div
                              :ref="el => setTagRef(el, product.Sku)"
                              class="tag-strip flex-1 min-w-0 flex flex-nowrap gap-1"
                              @scroll="updateScrollState(product.Sku)"
                            >
                              <span
                                v-for="tag in getProductTags(product)"
                                :key="tag"
                                data-tag
                                :title="tag"
                                class="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium whitespace-nowrap flex-shrink-0"
                              >{{ tag }}</span>
                            </div>
                            <button
                              v-if="tagScrollStates[product.Sku]?.hiddenCount > 0"
                              @click.stop="scrollTagsRight(product.Sku)"
                              class="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium hover:bg-gray-300 select-none"
                            >+{{ tagScrollStates[product.Sku].hiddenCount }}</button>
                          </div>
                        </div>
                        <div v-if="product.OnOrderQty" class="flex items-center gap-1.5 w-fit bg-yellow-400 rounded py-1 px-2">
                          <p>On Order: {{ parseOnOrderQty(product.OnOrderQty).qty }}</p>
                          <div v-if="parseOnOrderQty(product.OnOrderQty).eta" class="relative group">
                            <span class="text-xs bg-yellow-600 text-white px-1.5 py-0.5 rounded cursor-default font-semibold">ETA</span>
                            <div class="tooltip-tip">{{ parseOnOrderQty(product.OnOrderQty).eta }}</div>
                          </div>
                        </div>
                        <span :class="[product._bgColor, 'absolute right-2 bottom-2 px-2 py-1 text-xs font-medium rounded-full text-gray-800']">
                          {{ product.TemplateId }}
                        </span>
                      </div>
                    </div>

                    <div v-if="selectedSku === product.Sku"
                      class="absolute top-1/2 right-0 translate-x-full -translate-y-1/2 bg-white border rounded-lg shadow-md z-10 p-2 flex flex-col gap-2">

                      <div class="relative group">
                        <button
                          :disabled="!barcodeCount(product)"
                          class="flex items-center gap-2 text-xl"
                          :class="barcodeCount(product) ? 'text-purple-600 hover:text-gray-800' : 'text-gray-300 cursor-not-allowed'"
                          @click.stop="onCopyBarcodeClick(product)">
                          <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-copy" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">{{ barcodeCount(product) > 1 ? 'Copy barcode…' : 'Copy Barcode' }}</div>
                        <div v-if="barcodeMenu?.sku === product.Sku && barcodeMenu?.mode === 'copy'"
                          class="absolute right-full mr-2 top-0 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-30 text-left" @click.stop>
                          <p class="text-[11px] text-gray-400 px-2 py-1">Select a barcode to copy</p>
                          <button v-for="bc in product.Barcodes" :key="bc.Barcode"
                            class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-purple-50"
                            @click="chooseBarcode(product, bc.Barcode)">
                            <span class="font-mono text-xs">{{ bc.Barcode }}</span>
                            <i class="pi pi-copy text-purple-600 text-xs"></i>
                          </button>
                        </div>
                      </div>

                      <div class="relative group">
                        <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                          @click.stop="copyToClipboard(product.Sku, 'ItemCode')">
                          <span class="text-sm">Sku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Copy SKU</div>
                      </div>

                      <div class="relative group">
                        <button class="flex items-center gap-1 text-xl text-purple-600 hover:text-gray-800"
                          @click.stop="copyToClipboard(product.RetailSku, 'RetailSku')">
                          <span class="text-sm">RSku</span><i class="pi pi-copy" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Copy Retail SKU</div>
                      </div>

                      <div class="relative group">
                        <button
                          :disabled="!barcodeCount(product)"
                          class="flex items-center gap-2 text-xl select-none"
                          :class="barcodeCount(product) ? 'text-purple-600 hover:text-gray-800' : 'text-gray-300 cursor-not-allowed'"
                          @click.stop="onPrintBarcodeClick(product)">
                          <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">{{ barcodeCount(product) > 1 ? 'Print barcode…' : 'Print barcode' }}</div>
                        <div v-if="barcodeMenu?.sku === product.Sku && barcodeMenu?.mode === 'print'"
                          class="absolute right-full mr-2 top-0 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-1 z-30 text-left" @click.stop>
                          <p class="text-[11px] text-gray-400 px-2 py-1">Select a barcode to print</p>
                          <button v-for="bc in product.Barcodes" :key="bc.Barcode"
                            class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-purple-50"
                            @click="chooseBarcode(product, bc.Barcode)">
                            <span class="font-mono text-xs">{{ bc.Barcode }}</span>
                            <i class="pi pi-print text-purple-600 text-xs"></i>
                          </button>
                        </div>
                      </div>

                      <div class="relative group">
                        <button
                          :disabled="!barcodeCount(product)"
                          class="flex items-center gap-2 text-xl select-none"
                          :class="barcodeCount(product) ? 'text-purple-600 hover:text-gray-800' : 'text-gray-300 cursor-not-allowed'"
                          @click.stop="onPrintMultipleClick(product)">
                          <i class="pi pi-barcode" style="font-size: 16px;"></i><i class="pi pi-print" style="font-size: 16px;"></i><span class="text-xs font-semibold -ml-1">x#</span>
                        </button>
                        <div class="tooltip-tip tooltip-tip--left">Print multiple barcodes</div>

                        <div v-if="printTarget?.product.Sku === product.Sku"
                          class="absolute bottom-full right-0 mb-2 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-20"
                          @click.stop>
                          <input
                            :ref="el => { printCountInputEl = el }"
                            v-model.number="printTarget.count"
                            type="number" min="1" max="99"
                            class="w-14 px-2 py-1 bg-gray-100 rounded text-center text-sm outline-none"
                            @keydown.enter="confirmPrintWithCount"
                            @keydown.esc="printTarget = null"
                          />
                          <button class="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                            @click="confirmPrintWithCount">Print</button>
                          <button class="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                            @click="printTarget = null">✕</button>
                        </div>
                      </div>
                    </div>

                    <div class="flex justify-between bg-white rounded-lg my-2 p-3">
                      <p v-if="hasAnyValidCosts" class="text-sm"><strong>Cost:</strong> {{ product.Cost && product.Cost !== '-' ? `$${parseFloat(product.Cost).toFixed(2)}` : '-' }}</p>
                      <p v-if="inPos" class="text-sm"><strong>Price:</strong> {{ formatPrice(currentBranchPrice(product)) }}</p>
                      <p v-if="!inPos" class="text-sm"><strong>Gor Price:</strong> {{ formatPrice(getGorillaPrice(product)) }}</p>
                      <p v-if="!inPos" class="text-sm"><strong>Camo Price:</strong> {{ formatPrice(getCamoPrice(product)) }}</p>
                    </div>

                    <div class="mt-3 bg-white rounded-lg p-3">
                      <div class="grid grid-cols-3 font-semibold pb-2 mb-2">
                        <div>Branches</div>
                        <div class="text-center w-[50%]">Qty</div>
                        <div>Reconciled on</div>
                      </div>
                      <div
                        v-for="(branch, bIndex) in sortBranches(product.Branches.filter(b =>
                          !['SS01-Hubert-01 Operators', 'ZE01-Bermondsey-01 Operators', 'ZE02-Oshawa-01 Operators', 'AO01-Houston Operators', 'Qc1 Operators', 'Montreal Operators'].includes(b.Branch)
                        ))"
                        :key="branch.Branch + bIndex"
                        class="grid grid-cols-3 py-2"
                      >
                        <div>{{ branch.DisplayName }}</div>
                        <div class="px-2 py-1 rounded w-[50%] text-center"
                          :class="{
                            'bg-emerald-50 text-green-700': branch.Qty > 5,
                            'bg-cyan-50 text-cyan-700': branch.Qty > 0 && branch.Qty <= 5,
                            'bg-gray-50 text-gray-400': branch.Qty <= 0
                          }">{{ branch.Qty }}</div>
                        <div>{{ formatReconciliationDate(branch.LastReconciliation) || 'Not reconciled' }}</div>
                      </div>
                    </div>

                    <div v-if="product.Branches.some(b => ['SS01-Hubert-01 Operators', 'ZE01-Bermondsey-01 Operators', 'ZE02-Oshawa-01 Operators'].includes(b.Branch))"
                      class="mt-2 p-3 bg-white rounded-lg">
                      <div class="bg-white rounded-lg">
                        <div
                          v-for="(branch, bIndex) in sortBranches(product.Branches.filter(b =>
                            ['SS01-Hubert-01 Operators', 'ZE01-Bermondsey-01 Operators', 'ZE02-Oshawa-01 Operators'].includes(b.Branch)
                          ))"
                          :key="branch.Branch + bIndex"
                          class="grid grid-cols-3 py-2"
                        >
                          <div>{{ branch.DisplayName }}</div>
                          <div class="px-2 py-1 rounded w-[50%] text-center"
                            :class="{
                              'bg-emerald-50 text-emerald-700': branch.Qty > 5,
                              'bg-cyan-50 text-cyan-700': branch.Qty > 0 && branch.Qty <= 5,
                              'bg-gray-50 text-gray-400': branch.Qty <= 0
                            }">{{ branch.Qty }}</div>
                          <div>{{ formatReconciliationDate(branch.LastReconciliation) || 'Not reconciled' }}</div>
                        </div>
                      </div>
                    </div>

                    <div v-if="product.Branches.some(b => b.Branch === 'AO01-Houston Operators')"
                      class="mt-2 p-3 bg-white rounded-lg">
                      <div class="bg-white rounded-lg">
                        <p class="text-sm"><strong>CamoUSD: </strong>{{ getTexasPrice(product) }}</p>
                        <div class="grid grid-cols-3 py-2">
                          <div>TEX</div>
                          <div class="px-2 py-1 rounded w-[50%] text-center"
                            :class="{
                              'bg-emerald-50 text-emerald-700': product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty > 5,
                              'bg-cyan-50 text-cyan-700': product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty > 0 && product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty <= 5,
                              'bg-gray-50 text-gray-400': product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty === 0
                            }">
                            {{ product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.Qty }}
                          </div>
                          <div>
                            {{ product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.LastReconciliation
                              ? formatReconciliationDate(product.Branches.find(b => b.Branch === 'AO01-Houston Operators')?.LastReconciliation)
                              : 'Not reconciled' }}
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Sentinel + loading/cap/error/end states (mirror of the list view). -->
            <div ref="cardSentinel" class="h-px"></div>
            <div class="px-1 py-3" aria-live="polite">
              <div v-if="loadError" class="text-center">
                <button @click="retryLoad" class="px-4 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">Retry</button>
              </div>
              <div v-else-if="isRequsetBeingMade || (hasMoreData && !atAutoCap)">
                <div class="elastic-track"><div class="elastic-bar"></div></div>
                <p v-if="isLoadingMore || isPreloading" class="text-center text-xs text-gray-400 mt-1">Loading… {{ responseData.length }} items</p>
              </div>
              <div v-else-if="hasMoreData && atAutoCap" class="text-center">
                <button @click="loadMore({ override: true })" class="px-4 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">Load more</button>
              </div>
              <p v-else class="text-center text-xs text-gray-400">End of results</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.tooltip-tip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: #fff;
  font-size: 11px;
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: 5px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 30;
}

.tooltip-tip--left {
  left: auto;
  right: 0;
  transform: none;
}

.group:hover .tooltip-tip {
  opacity: 1;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}
input[type="number"] { -moz-appearance: textfield; }

.tag-strip {
  overflow-x: scroll;
  scrollbar-width: none;
}
.tag-strip::-webkit-scrollbar { display: none; }

/* Elastic indeterminate loading bar — the moving segment stretches/contracts as it sweeps,
   giving an "elastic" feel. Used both while fetching and while it's unknown whether more
   results remain (so the user always knows more may still be coming). */
.elastic-track {
  position: relative;
  height: 4px;
  width: 100%;
  background: #ede9fe; /* purple-100 */
  border-radius: 9999px;
  overflow: hidden;
}
.elastic-bar {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 9999px;
  background: #7c3aed; /* purple-600 */
  animation: elastic-slide 1.15s cubic-bezier(0.65, 0, 0.35, 1) infinite;
}
@keyframes elastic-slide {
  0%   { left: -40%; width: 40%; }
  50%  { left: 25%;  width: 60%; } /* stretch at mid-sweep */
  100% { left: 100%; width: 40%; }
}

/* Lightweight virtualization: skip layout/paint for off-screen rows & cards. `auto` in
   contain-intrinsic-size remembers each element's last-rendered height, so scrollbar size
   and scroll position stay stable as items enter/leave the viewport. */
.ci-vrow {
  content-visibility: auto;
  contain-intrinsic-size: auto 96px;
}
.ci-vcard {
  content-visibility: auto;
  contain-intrinsic-size: auto 320px;
}

/* Containing border wrapping a whole template group (header + all its variant rows).
   Applied at cell level so it renders in the table's default separate-border model. */
/* The group's box border. The last real row is nth-last-child(2) because the last child is
   the transparent .ci-group-gap spacer that separates one group card from the next. */
tbody.ci-group > tr:not(.ci-group-gap) > td:first-child { border-left: 2px solid #e5e7eb; }
tbody.ci-group > tr:not(.ci-group-gap) > td:last-child  { border-right: 2px solid #e5e7eb; }
tbody.ci-group > tr:first-child > td { border-top: 2px solid #e5e7eb; }
tbody.ci-group > tr:nth-last-child(2) > td { border-bottom: 2px solid #e5e7eb; }
tbody.ci-group--open > tr:not(.ci-group-gap) > td:first-child { border-left-color: #d8b4fe; }
tbody.ci-group--open > tr:not(.ci-group-gap) > td:last-child  { border-right-color: #d8b4fe; }
tbody.ci-group--open > tr:first-child > td { border-top-color: #d8b4fe; }
tbody.ci-group--open > tr:nth-last-child(2) > td { border-bottom-color: #d8b4fe; }
/* Round the group's outer corners (like the card view) via the corner cells. */
tbody.ci-group > tr:first-child > td:first-child { border-top-left-radius: 14px; }
tbody.ci-group > tr:first-child > td:last-child  { border-top-right-radius: 14px; }
tbody.ci-group > tr:nth-last-child(2) > td:first-child { border-bottom-left-radius: 14px; }
tbody.ci-group > tr:nth-last-child(2) > td:last-child  { border-bottom-right-radius: 14px; }
/* A little inner breathing room at the top/bottom of each group box. */
tbody.ci-group > tr:first-child > td { padding-top: 4px; }
tbody.ci-group > tr:nth-last-child(2) > td { padding-bottom: 6px; }
/* The inter-group gap itself: no borders, just height. */
tbody.ci-group > tr.ci-group-gap > td { height: 12px; padding: 0; border: 0; background: transparent; }

/* Skeleton shimmer for first-load placeholders. */
.skeleton {
  background: linear-gradient(90deg, #eef0f2 25%, #e3e6e9 37%, #eef0f2 63%);
  background-size: 400% 100%;
  animation: skeleton-shimmer 1.4s ease infinite;
}
@keyframes skeleton-shimmer {
  0%   { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
</style>
